import { primaryHoldReason } from './holdReasonAudience';

/**
 * The record a held item is fixed on. Never the item itself: it is derived, so the only correction
 * that survives the next projection is one made to what it came from.
 */
export const FIX_KIND = {
  CLIENT: 'client',
  FUNDING_SOURCE: 'fundingSource',
  CHARGE_MASTER: 'chargeMaster',
  SESSION_ACTIVITY: 'sessionActivity',
  /** One session's missing service or place, without opening the session. */
  SESSION_DELIVERY: 'sessionDelivery',
  /** Every held session a plan ran, at once, and the plan itself if the user chooses. */
  SESSION_PLAN: 'sessionPlan',
  TREATMENT_SERVICE: 'treatmentService',
  STAFF: 'staff',
};

/**
 * Which part of the record holds the fact, in this context's words. The host maps these onto its
 * own editor's tabs, so the domain never learns how an editor is laid out.
 */
export const FIX_SECTION = {
  COVERAGE: 'coverage',
  AUTHORIZATIONS: 'authorizations',
  DIAGNOSES: 'diagnoses',
  DEMOGRAPHICS: 'demographics',
  PROVIDER: 'provider',
  QUALIFICATIONS: 'qualifications',
  TREATMENT_SERVICES: 'treatmentServices',
  PLACE_OF_SERVICE: 'placeOfService',
};

const COVERAGE_REASONS = [
  'no_coverage',
  'coverage_unverified',
  'coverage_on_hold',
  'coverage_policyholder_incomplete',
  'coverage_policyholder_unresolved',
];
const PAYER_REASONS = ['incomplete_funder_config', 'unsupported_bills_under', 'no_modifier_tier', 'too_many_modifiers'];
const CHARGE_MASTER_REASONS = ['no_charge_master', 'no_charge'];
/**
 * Fixed on the session. A tier the payer does not pay is fixed by reassigning the session to
 * somebody whose tier it pays, not by changing the payer's rules.
 */
const ACTIVITY_REASONS = [
  'duplicate_treatment_service',
  'no_performer',
  'tier_not_payable',
];
const TREATMENT_SERVICE_REASONS = ['not_billable', 'no_code_source', 'code_not_valid_on_date', 'manually_entered_code'];

const target = (kind, recordId, section = null) => (recordId ? { kind, recordId, section } : null);

/**
 * Where a held item gets fixed, or null when nothing a clinic can open would fix it: a ready item,
 * a support-only reason, or a reference the item does not carry.
 *
 * @param {import('../model/BillingItem.types').BillingItem} item
 * @returns {{kind: string, recordId: string, section: string|null}|null}
 */
export function fixFor(item) {
  const reason = primaryHoldReason(item.holdReasons);

  if (COVERAGE_REASONS.includes(reason)) {
    return target(FIX_KIND.CLIENT, item.clientId, FIX_SECTION.COVERAGE);
  }
  if (reason === 'held_for_auth') {
    return target(FIX_KIND.CLIENT, item.clientId, FIX_SECTION.AUTHORIZATIONS);
  }
  if (reason === 'no_diagnosis') {
    return target(FIX_KIND.CLIENT, item.clientId, FIX_SECTION.DIAGNOSES);
  }
  if (reason === 'patient_demographics_incomplete') {
    return target(FIX_KIND.CLIENT, item.clientId, FIX_SECTION.DEMOGRAPHICS);
  }
  // A coverage naming no payer at all resolves no funding source, and is fixed on the coverage.
  if (reason === 'no_funder_as_of') {
    return item.fundingSourceId
      ? target(FIX_KIND.FUNDING_SOURCE, item.fundingSourceId)
      : target(FIX_KIND.CLIENT, item.clientId, FIX_SECTION.COVERAGE);
  }
  if (PAYER_REASONS.includes(reason)) {
    return target(FIX_KIND.FUNDING_SOURCE, item.fundingSourceId);
  }
  // One sheet per clinic, so the clinic is the record.
  if (CHARGE_MASTER_REASONS.includes(reason)) {
    return target(FIX_KIND.CHARGE_MASTER, item.clinicId);
  }
  // One row is one session: a biller fixing it from the list means this session, not its plan's.
  if (reason === 'no_treatment_service') {
    return target(FIX_KIND.SESSION_DELIVERY, item.sessionActivityId, FIX_SECTION.TREATMENT_SERVICES);
  }
  if (reason === 'no_place_of_service') {
    return target(FIX_KIND.SESSION_DELIVERY, item.sessionActivityId, FIX_SECTION.PLACE_OF_SERVICE);
  }
  if (ACTIVITY_REASONS.includes(reason)) {
    return target(FIX_KIND.SESSION_ACTIVITY, item.sessionActivityId);
  }
  if (TREATMENT_SERVICE_REASONS.includes(reason)) {
    return target(FIX_KIND.TREATMENT_SERVICE, item.treatmentServiceId);
  }
  if (reason === 'no_rendering_provider') {
    return target(FIX_KIND.STAFF, item.performingUserId, FIX_SECTION.PROVIDER);
  }
  if (reason === 'performer_no_qualification') {
    return target(FIX_KIND.STAFF, item.performingUserId, FIX_SECTION.QUALIFICATIONS);
  }
  return null;
} // fixFor()
